import { Component, inject, Input } from '@angular/core';
import { Router } from '@angular/router';
import { getUrl, hasLink } from '@ngxp/rest';
import {
	VerfahrenFacade,
	VerfahrenLinkRel,
	VerfahrenMessages,
	VerfahrenResource,
} from '@schir-int-client/verfahren-shared';
import { AmtlichenAusdruckErstellenDialogComponent } from '@schir-int-client/alternative-text';
import { DialogService, HandlesBackdropClickAndEscapeKey, NotizDialogComponent } from '@schir-int-client/tech';
import { AktenzeichenRendererProvider, AktenzeichenUtil } from '@schir-int-client/aktenzeichen-shared';
import { firstValueFrom } from 'rxjs';
import { StandortAkteComponent } from '../standort-akte/standort-akte.component';
import { ZaehlblattErfassenComponent } from '../zaehlblatt/zaehlblatt-erfassen.component';
import { ZaehlblattAnzeigenComponent } from '../zaehlblatt/zaehlblatt-anzeigen.component';
import { KennzeichenComponent } from '../../kennzeichen/kennzeichen.component';
import { ZugeordneteVerfahrenDialogComponent } from '../zugeordnete-verfahren-dialog/zugeordnete-verfahren-dialog.component';
import { getId } from '../../../../../test-helpers/src/lib/resource-helper';

@Component({
	selector: 'schir-int-client-button-with-menu',
	templateUrl: 'button-with-menu.component.html',
	styleUrl: 'button-with-menu.component.scss',
	standalone: false,
})
export class ButtonWithMenuComponent extends HandlesBackdropClickAndEscapeKey<
	| NotizDialogComponent
	| AmtlichenAusdruckErstellenDialogComponent
	| StandortAkteComponent
	| ZaehlblattErfassenComponent
	| ZaehlblattAnzeigenComponent
	| KennzeichenComponent
	| ZugeordneteVerfahrenDialogComponent
> {
	private facade = inject(VerfahrenFacade);
	private router = inject(Router);
	private renderer = inject(AktenzeichenRendererProvider).getRenderer();
	@Input() verfahren: VerfahrenResource;
	readonly linkRel = VerfahrenLinkRel;

	constructor(protected dialogService: DialogService) {
		super(dialogService);
	}

	private renderAktenzeichen() {
		return this.renderer.render(AktenzeichenUtil.parse(this.verfahren));
	}

	openNotizDialog(event: MouseEvent) {
		this.preventMenuClosingForKeyboard(event);

		this.matDialogRef = this.dialogService.openEditorDialog(this, NotizDialogComponent, {
			data: {
				title: 'Notizen zum Verfahren ' + this.renderAktenzeichen(),
				notiz: this.verfahren.notiz,
				verfahren: this.verfahren,
				readonly: !hasLink(this.verfahren, VerfahrenLinkRel.NOTIZ),
			},
		});

		const editor = <NotizDialogComponent>this.matDialogRef.componentInstance;
		firstValueFrom(editor.notizEvent).then(data => this.updateNotiz(data));
	}

	updateNotiz(notiz: string) {
		this.facade.updateNotiz(notiz, getUrl(this.verfahren, VerfahrenLinkRel.NOTIZ));
	}

	hasRegisterBlatt(): boolean {
		return (
			hasLink(this.verfahren, VerfahrenLinkRel.BINNENSCHIFFSREGISTER) ||
			hasLink(this.verfahren, VerfahrenLinkRel.SEESCHIFFSREGISTER) ||
			hasLink(this.verfahren, VerfahrenLinkRel.SCHIFFSBAUREGISTER) ||
			hasLink(this.verfahren, VerfahrenLinkRel.LUFTFAHRTPFANDRECHTSREGISTER) ||
			hasLink(this.verfahren, VerfahrenLinkRel.ERSATZTEILLAGER)
		);
	}

	openRegisterBlatt() {
		this.router.navigate(['/verfahrenDetail', getId(this.verfahren)]);
	}

	openAmtlichenAuszugErstellenDialog() {
		this.dialogService.openEditorDialog(this, AmtlichenAusdruckErstellenDialogComponent);
	}

	downloadSchiffszertifikat() {
		this.facade.downloadSchiffszertifikat();
	}

	downloadSchiffsbrief() {
		this.facade.downloadSchiffsbrief();
	}

	markAsAbgelehnt(event: MouseEvent) {
		this.preventMenuClosingForKeyboard(event);
		this.facade.markAsAbgelehnt(this.verfahren);
	}

	openUsignalZuordnenDialog() {
		const aktenzeichen = AktenzeichenUtil.parse(this.verfahren.aktenzeichen);

		firstValueFrom(
			this.dialogService.openDialogYesNo(
				VerfahrenMessages.CONFIRM_ASSIGN_USIGNAL.replace('{aktenzeichen}', this.renderer.render(aktenzeichen))
			)
		).then(data => {
			if (data.agree) {
				this.facade.assignUSignal(this.verfahren);
			}
		});
	}

	private preventMenuClosingForKeyboard(event: MouseEvent) {
		if (event.detail === 0) {
			event.stopPropagation();
		}
	}

	openStandortAkteDialog() {
		this.dialogService.openEditorDialog(this, StandortAkteComponent, this.getConfig());
	}

	openZaehlblattErfassenDialog() {
		this.dialogService.openEditorDialog(this, ZaehlblattErfassenComponent, this.getConfig());
	}

	openZaehlblattAnzeigenDialog() {
		this.dialogService.openEditorDialog(this, ZaehlblattAnzeigenComponent, this.getConfig());
	}

	openKennzeichenDialog() {
		this.dialogService.openEditorDialog(this, KennzeichenComponent, this.getConfig());
	}

	openZugeordneteVerfahrenDialog() {
		this.dialogService.openEditorDialog(this, ZugeordneteVerfahrenDialogComponent, this.getConfig());
	}

	private getConfig() {
		return {
			data: {
				verfahren: this.verfahren,
				aktenzeichen: this.renderAktenzeichen(),
			},
		};
	}
}
